import { Table, TableBody } from '@vtex/shoreline'
import { t } from 'i18next'

import ListPagination from '~/components/ListPagination'
import { useShowWalletOriginsColumn } from '~/modules/retailMedia/hooks/useShowWalletOrigins'

import { CheckingAccountTableHeader } from './CheckingAccountTableHeader'
import { CheckingAccountTableRow } from './CheckingAccountTableRow'

import styles from './styles.module.css'

const BASE_COLUMN_WIDTHS = [
  'calc(var(--sl-space-3) + 2.25rem)',
  'minmax(10rem, 1fr)',
  '9rem',
  '12.5rem',
  '3.5rem'
]

const WITHOUT_ORIGINS_COLUMN_WIDTHS = [
  'minmax(10rem, 1fr)',
  'minmax(10rem, 1fr)',
  '3.5rem'
]

const WITH_RECHARGE_COLUMN_WIDTHS = [
  'calc(var(--sl-space-3) + 2.25rem)',
  'minmax(10rem, 1fr)',
  '9rem',
  '12.5rem',
  'minmax(6rem, auto)',
  '3.5rem'
]

const WITHOUT_ORIGINS_WITH_RECHARGE_COLUMN_WIDTHS = [
  'minmax(10rem, 1fr)',
  'minmax(10rem, 1fr)',
  'minmax(6rem, auto)',
  '3.5rem'
]

const getColumnWidths = (
  showOriginsColumn: boolean,
  showRechargeColumn: boolean
) => {
  if (showOriginsColumn) {
    return showRechargeColumn ? WITH_RECHARGE_COLUMN_WIDTHS : BASE_COLUMN_WIDTHS
  }

  return showRechargeColumn
    ? WITHOUT_ORIGINS_WITH_RECHARGE_COLUMN_WIDTHS
    : WITHOUT_ORIGINS_COLUMN_WIDTHS
}

type CheckingAccountTableContentProps = {
  rows: GetCheckingAccountWithAccountInfoResponse[]
  total: number
  pages: number
  page: number
  perPage: number
  accountKey: 'advertiser' | 'publisher'
  showRechargeColumn: boolean
  expandedIds: Set<string>
  activeSortKey: string
  activeSortDirection: string
  onSort: (data: OnSortProps) => void
  onToggleExpanded: (id: string) => void
  onPagination: (page: number) => void
  onItemsPerPageChange: ({ value }: { value: number }) => void
}

export const CheckingAccountTableContent = ({
  rows,
  total,
  pages,
  page,
  perPage,
  accountKey,
  showRechargeColumn,
  expandedIds,
  activeSortKey,
  activeSortDirection,
  onSort,
  onToggleExpanded,
  onPagination,
  onItemsPerPageChange
}: CheckingAccountTableContentProps) => {
  const showOriginsColumn = useShowWalletOriginsColumn()

  return (
    <>
      <div
        className={styles.tableCard}
        data-has-origins-column={showOriginsColumn ? 'true' : undefined}
      >
        <Table
          columnWidths={getColumnWidths(showOriginsColumn, showRechargeColumn)}
        >
          <CheckingAccountTableHeader
            showRechargeColumn={showRechargeColumn}
            activeSortKey={activeSortKey}
            activeSortDirection={activeSortDirection}
            onSort={onSort}
          />
          <TableBody>
            {rows.map(row => (
              <CheckingAccountTableRow
                key={row.id}
                row={row}
                accountKey={accountKey}
                showRechargeColumn={showRechargeColumn}
                isOpen={expandedIds.has(row.id)}
                onToggle={() => onToggleExpanded(row.id)}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {pages > 1 ? (
        <div className={styles.footer}>
          <ListPagination
            total={total}
            label={t(`rm:${total > 1 ? 'wallets' : 'wallet'}`)}
            currentPage={page}
            itemsPerPage={perPage}
            menuPlacement="top"
            onClickPagination={onPagination}
            onItemsPerPageChange={onItemsPerPageChange}
          />
        </div>
      ) : null}
    </>
  )
}
